import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface ServiceArea {
  id: string;
  user_id: string;
  zip_code: string;
  is_golden_zone: boolean;
  created_at: string;
}

export function useServiceAreas() {
  const { user } = useAuth();
  const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchServiceAreas = useCallback(async () => {
    if (!user) { setServiceAreas([]); setLoading(false); return; }
    const { data } = await supabase
      .from("service_areas")
      .select("*")
      .order("created_at", { ascending: false });
    setServiceAreas((data as any[]) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchServiceAreas(); }, [fetchServiceAreas]);

  const addServiceArea = useCallback(async (zip_code: string, is_golden_zone = false) => {
    if (!user) return;
    const { error } = await supabase.from("service_areas").insert({
      user_id: user.id,
      zip_code,
      is_golden_zone,
    } as any);
    if (error) throw error;
    await fetchServiceAreas();
  }, [user, fetchServiceAreas]);

  const updateServiceArea = useCallback(async (id: string, updates: Partial<ServiceArea>) => {
    const { error } = await supabase.from("service_areas").update(updates as any).eq("id", id);
    if (error) throw error;
    await fetchServiceAreas();
  }, [fetchServiceAreas]);

  const deleteServiceArea = useCallback(async (id: string) => {
    const { error } = await supabase.from("service_areas").delete().eq("id", id);
    if (error) throw error;
    await fetchServiceAreas();
  }, [fetchServiceAreas]);

  return { serviceAreas, loading, addServiceArea, updateServiceArea, deleteServiceArea, refetch: fetchServiceAreas };
}
